<template>
    <q-table class='Report'
        :dark='false'
        :data='qtableData'
        :columns='qtableColumns'
        virtual-scroll
        :virtual-scroll-item-size="48"
        :pagination="qtablePagination"
        @virtual-scroll="handleQtableScroll"
        :loading='qtableLoading'
        :selected.sync="report.selectedRows"
        :selection="report.selectionMode"
        @selection="handleQtableSelect"
        :style="'height: ' + qtableHeight"
    >
        <template v-slot:header="props">
            <q-tr :props="props">
                <q-th v-if='report.selectionMode != "none"'></q-th>
                <q-th v-for="(col, index) in props.cols" :key="col.name" :props="props">
                    <ReportHeaderCell :columnData='col.columnData':isLast="index === props.cols.length - 1" @update-sort='updateSort' @update-selected-facet='updateSelectedFacet'/>
                </q-th>
            </q-tr>
        </template>

        <template v-slot:bottom>
            <span> {{ format(report.results, 'int') }} records matched</span>
        </template>

        <template v-slot:top>
            <ReportTools
                :textFilter='report.textFilter'
                :tableIsLoading='report.loading'
                :reportButtons='report.reportButtons'
                @update-text-filter='updateTextFilter'
                @report-button-clicked='handleReportButtonClick'
                :reportTitle='report.title'
            >
                <template v-slot:buttons>
                    <slot name='buttons'></slot>
                </template>
            </ReportTools>
        </template>

        <template v-slot:body-cell-rowActions="props">
            <q-td :props="props">
                <q-btn-dropdown color="default">
                    <template v-slot:label>
                        <ThreeDotsVerticalIcon />
                    </template>
                    <q-list>
                        <q-item
                            v-for='action in report.rowActions'
                            :key='props.row.id'
                            clickable
                            v-close-popup
                            @click="handleRowAction(action.action)">
                            <q-item-section>
                                <q-item-label>{{ action.label }}</q-item-label>
                            </q-item-section>
                        </q-item>
                    </q-list>
                </q-btn-dropdown>
            </q-td>
      </template>
    </q-table>
</template>


<script>
    import { QTable, QTr, QTh } from 'quasar'
    import SortedAscIcon from '../../icons/SortedAscIcon.vue';
    import SortedDescIcon from '../../icons/SortedDescIcon.vue';
    import FunnelIcon from '../../icons/FunnelIcon.vue';
    import ThreeDotsVerticalIcon from '../../icons/ThreeDotsVerticalIcon.vue';
    import ReportHeaderCell from './ReportHeaderCell.vue'
    import ReportTools from './ReportTools.vue'
    import Formatter from '../../../js/format.js';

    export default {
        name: 'Report',
        components: {
            QTable,
            QTr,
            QTh,
            ReportHeaderCell,
            ReportTools,
            SortedAscIcon,
            SortedDescIcon,
            FunnelIcon,
            ThreeDotsVerticalIcon
        },
        props: ['initialReportState'],
        data() {
            return{
                report: {
                    model: this.$props.initialReportState.model,
                    columns: this.$props.initialReportState.columns || {},
                    availableColumns: [],
                    textFilter: this.$props.initialReportState.textFilter || '',
                    rows: this.$props.initialReportState.rows || 200,
                    page: this.$props.initialReportState.page || 0,
                    data: [],
                    results: null,
                    selectedFacets: this.$props.initialReportState.selectedFacets || [],
                    sorts: this.$props.initialReportState.sorts || {},
                    updated: this.$props.initialReportState.updated,
                    modified: false,
                    loading: false,
                    selectedRows: this.$props.initialReportState.selectedRows || [],
                    selectionMode: this.$props.initialReportState.selectionMode || 'none',
                    allSelected: false,
                    height: this.$props.initialReportState.height || "calc(100vh - 190px)",
                    title: this.$props.initialReportState.title || 'Untitled Report',
                    reportButtons: this.$props.initialReportState.reportButtons || [],
                    rowActions: this.$props.initialReportState.rowActions || []
                }
            }
        },
        computed: {
            qtableData(){
                if(this.report.rowActions.length > 0) return this.report.data.map(row => { return {...row, ...{reportActions: true }}})
                else return this.report.data
            },
            qtableColumns(){
                var columns = Object.values(this.report.columns).map(col => { return {
                    name: col.name,
                    field: col.name,
                    columnData: col
                }})
                if(this.report.rowActions.length > 0){
                    columns.push({
                        name: 'rowActions',
                        field: 'rowActions',
                        columnData: {
                            dataType: 'actions',
                            displayName: '',
                            facets: [],
                            metrics: [],
                            name: 'rowActions',
                            rangeGap: null,
                            sort: null,
                            sortPriority: 0,
                            sortable: 0
                        }
                    })
                }

                return columns
            },
            qtablePagination(){
                return {
                    rowsPerPage: this.report.rows,
                    rowsNumber: this.report.results,
                    page: this.report.page,
                    sortBy: 'desc',
                    descending: false
                }
            },
            qtableLoading(){
                return this.report.loading
            },
            qtableHeight(){
                return this.report.height
            }
        },
        mounted(){
            this.updateReport();
        },
        methods: {
            parseReportForSend(pageOnly = false) {
                var parsed = {...this.report}
                delete parsed.availableColumns
                delete parsed.data
                delete parsed.results
                delete parsed.updated
                delete parsed.modified
                parsed.pageOnly = pageOnly
                return parsed
            },
            parseReportResponse(response, pageOnly){
                var formattedData = Formatter.formatReportRows(response.data, pageOnly ? this.report.columns : response.selectedColumns,)
                this.report = {...this.report, ...{
                    availableColumns: pageOnly ? this.report.availableColumns : response.availableColumns,
                    columns: pageOnly ? this.report.columns : response.selectedColumns,
                    data: pageOnly ? this.report.data.concat(formattedData) : formattedData,
                    textFilter: response.textFilter,
                    rows: response.rows,
                    page: response.page,
                    results: response.results,
                    selectedFacets: response.selectedFacets,
                    sorts: response.sorts,
                    selectedRows: this.report.allSelected ? this.report.data.concat(response.data) : this.report.selectedRows
                }}
            },
            updateReportAttribute(update, post = true){
                var pageOnly = (Object.values(update).length == 1 && 'page' in update)
                if(!pageOnly) update = {...update, ...{page: 1}}
                this.report = {...this.report, ...update }
                if(post) this.updateReport(pageOnly)
            },
            updateReport(pageOnly){
                this.updateReportAttribute({loading: true}, false)
                this.$api.post('/api/reporting/json', this.parseReportForSend(pageOnly))
                    .then((response) => this.parseReportResponse(response, pageOnly))
                    .catch((error) => console.log(error))
                    .finally(() => this.updateReportAttribute({loading: false}, false))
            },
            saveReport(){
                this.$store.dispatch('Reporting/saveReport', this.parseReportForSend());
                this.modified = false;
            },
            updateSort(payload){
                if(payload.append) var newSort = {...this.report.sorts};
                else var newSort = {};
                newSort[payload.column] = {direction: payload.direction, order: Object.keys(newSort).length}
                this.updateReportAttribute({sorts: newSort}, true)
            },
            updateSelectedFacet(payload){
                var newSelectedFacets = [...this.report.selectedFacets];
                var match = newSelectedFacets.findIndex(facet => facet.column == payload.column && facet.value == payload.value);
                if(payload.selected){
                    if(match != -1) return;
                    newSelectedFacets.push({column: payload.column, value: payload.value});
                }else{
                    if(!match == -1) return;
                    newSelectedFacets.splice(match, 1);
                }
                this.updateReportAttribute({selectedFacets: newSelectedFacets}, true);
            },
            updateTextFilter(payload){
                this.updateReportAttribute({textFilter: payload || ''}, true)
            },
            handleQtableScroll({to, ref}){
                const lastIndex = this.report.data.length - 1
                if (this.report.loading !== true && (this.report.page + 1) < Math.ceil(this.report.results / this.report.rows) && to >= (this.report.data.length - 100)) {
                    this.updateReportAttribute({page: this.report.page + 1}, true);
                }
            },
            handleQtableSelect({ rows, added, evt }){
                if(rows.length == this.report.data.length) this.updateReportAttribute({allSelected: added}, false)
            },
            format(value, datatype){
                return value ? Formatter.format(value, datatype) : ''
            },
            handleReportButtonClick(payload){
                this.$emit(payload.action, this.report.selectedRows)
            },
            handleRowAction(payload){
                this.$emit(payload.action, payload.id)
            }
        }
    }
</script>

<style lang="scss">
    @use '~quasar/src/css/index.sass';
    //@import '~material-design-icons/iconfont/material-icons.css';
</style>
